import { SearchX } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import ModernPageLayout from "@/components/layout/ModernPageLayout";
import { DataTable } from "@/components/ui/v2/table/data-table";
import { TableActiveFilters } from "@/components/ui/v2/table/table-active-filters";
import { ROUTES } from "@/constants/routes";
import { useClient } from "@/features/clients/api/useClient";
import { useVacancies } from "@/features/vacancies/api/useVacancies";
import { useVacancyFilters } from "@/features/vacancies/api/useVacancyFilters";
import { useVacancyTableColumns } from "@/features/vacancies/api/useVacancyTableColumns";
import { VacanciesFiltersDrawer } from "@/features/vacancies/pages/overview/components/VacanciesFiltersDrawer";
import { VacanciesTableToolbar } from "@/features/vacancies/pages/overview/components/VacanciesTableToolbar";
import { usePageSearchParams } from "@/lib/hooks/usePageSearchParams";
import { usePageSizeSearchParams } from "@/lib/hooks/usePageSizeSearchParams";
import { useSearchSearchParams } from "@/lib/hooks/useSearchSearchParams";
import {
  useSortSearchParams,
  useTableSortHandler,
} from "@/lib/hooks/useSortSearchParams";
import { clearFilterValues, removeFilterValue } from "@/lib/utils/filterParams";
import { VacancyType } from "@/types/vacancy/VacancyType";

const OverviewVacanciesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { orderBy, descending } = useSortSearchParams();
  const { page, setPage } = usePageSearchParams();
  const { pageSize, setPageSize } = usePageSizeSearchParams();
  const {
    searchValue,
    debouncedSearchValue,
    setSearchValue,
  } = useSearchSearchParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clientId } = useParams();
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const city = searchParams.getAll("city");
  const country = searchParams.getAll("country");
  const department = searchParams.getAll("department");
  const field = searchParams.getAll("field");
  const includeUnpublished = searchParams.get("showUnpublished") === "true";
  const channels = searchParams.getAll("channels");
  const publicationChannels = searchParams.getAll("publicationChannels");
  const company = searchParams.getAll("company");
  const district = searchParams.getAll("district");

  const {
    data: vacancies,
    isLoading: vacanciesLoading,
    isFetching: vacanciesFetching,
  } = useVacancies({
    city: city.length > 0 ? city : undefined,
    country: country.length > 0 ? country : undefined,
    department: department.length > 0 ? department : undefined,
    field: field.length > 0 ? field : undefined,
    channels: channels.length > 0 ? channels : undefined,
    publicationChannels:
      publicationChannels.length > 0 ? publicationChannels : undefined,
    company: company.length > 0 ? company : undefined,
    district: district.length > 0 ? district : undefined,
    descending,
    orderBy,
    page,
    pageSize,
    search: debouncedSearchValue,
    includeUnpublished,
    clientId,
  });

  const { data: filters } = useVacancyFilters({
    clientId,
    search: debouncedSearchValue,
    includeUnpublished,
  });
  const { data: client } = useClient(clientId);
  const { vacancyTableColumns } = useVacancyTableColumns();
  const handleSort = useTableSortHandler<VacancyType>();

  const handleRowClick = (vacancy: VacancyType) => {
    const basePath = clientId
      ? `/clients/${clientId}/vacancies`
      : `/${ROUTES.vacancies}`;
    const queryString = searchParams?.toString();
    navigate(
      `${basePath}/${vacancy.id}${queryString ? `?${queryString}` : ""}`,
    );
  };

  const activeFilters = {
    city: city || [],
    country: country || [],
    department: department || [],
    field: field || [],
    channels: channels || [],
    publicationChannels: publicationChannels || [],
    company: company || [],
    district: district || [],
  };

  const handleFilters = () => {
    setIsFiltersOpen(true);
  };

  const hasActiveFilters = Object.values(activeFilters).some(
    (filters) => filters.length > 0,
  );

  const addedFilters = Object.entries(activeFilters).flatMap(([key, values]) =>
    values.map((value) => {
      let filterLabel: string | undefined;
      if (filters) {
        const filterKey = key as keyof typeof filters;
        if (filters[filterKey]) {
          const filter = filters[filterKey]?.find((f) => f.value === value);
          filterLabel = filter?.label;
        }
      }

      return {
        key,
        label: t(`common.${key}`),
        value,
        valueLabel: filterLabel ?? value,
      };
    }),
  );

  const handleRemoveFilter = (filter: {
    key: string;
    value: string | number;
  }) => {
    const newParams = removeFilterValue(
      searchParams,
      filter.key,
      filter.value.toString(),
    );

    if (newParams.get("page") !== "1") {
      newParams.set("page", "1");
    }

    setSearchParams(newParams);
  };

  const handleClearFilters = () => {
    setSearchParams(clearFilterValues(searchParams, Object.keys(activeFilters)));
  };

  return (
    <ModernPageLayout
      title={t("common.vacancies")}
      subtitle={t("common.vacanciesSubtitle")}
    >
      <div className="flex flex-col gap-8">
        <VacanciesTableToolbar
          clientId={clientId}
          onSearchChange={setSearchValue}
          onFiltersClick={handleFilters}
          searchValue={searchValue}
          client={client}
          showUnpublished={includeUnpublished}
          onShowUnpublishedChange={(checked) => {
            const newParams = new URLSearchParams(searchParams);
            if (checked) {
              newParams.set("showUnpublished", "true");
            } else {
              newParams.delete("showUnpublished");
            }
            setSearchParams(newParams);
          }}
        />

        {hasActiveFilters && (
          <TableActiveFilters
            filters={addedFilters}
            onRemoveFilter={handleRemoveFilter}
            onClearFilters={handleClearFilters}
          />
        )}

        <DataTable
          columns={vacancyTableColumns}
          data={vacancies?.data}
          isLoading={vacanciesLoading || vacanciesFetching}
          onRowClick={handleRowClick}
          orderBy={orderBy}
          descending={descending}
          onSort={handleSort}
          page={page}
          pageSize={pageSize}
          totalRecords={vacancies?.totalRecords ?? 0}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          getRowId={(vacancy) => vacancy.id}
          emptyState={{
            icon: SearchX,
            title: t("common.noVacancies", "No vacancies found"),
            description: t(
              "common.vacanciesNoDataDescription",
              "No vacancies match your current filters",
            ),
          }}
        />
      </div>

      <VacanciesFiltersDrawer
        open={isFiltersOpen}
        onOpenChange={setIsFiltersOpen}
        searchParams={searchParams}
        clientId={clientId}
        includeUnpublished={includeUnpublished}
        searchValue={debouncedSearchValue}
      />
    </ModernPageLayout>
  );
};

export default OverviewVacanciesPage;
